/**
 * ANIMATE-mode posing. FK and IK both land in `livePose`; keying copies from `livePose` into the
 * clip through `clipOps.setKeyframe`, so an IK-authored pose serializes exactly like an
 * FK-authored one (SPEC "IK System → Critical serialization rule").
 */

import {
  DEFAULT_EASING,
  identityQuat,
  normalizeQuat,
  quatEquals,
  setKeyframe,
} from '../../shared';
import type { CanonicalBoneId, Pose, QuaternionTuple } from '../../shared';
import {
  commitClipEdit,
  evaluatedHipsY,
  evaluatedLive,
  evaluatedPose,
  showToast,
  type ActionContext,
} from './context';

/** Tolerance for "this bone differs from the evaluated pose" when keying every posed bone. */
const POSE_EPSILON = 1e-6;

/** Coalesce key the hips-height field and the viewport handle share (one drag, one undo entry). */
export const HIPS_Y_COALESCE_KEY = 'hips-y';

export interface PoseActions {
  setLivePoseBone(
    bone: CanonicalBoneId,
    rotation: QuaternionTuple,
    options?: { coalesceKey?: string },
  ): void;
  /**
   * Replace the whole working pose. `hipsY` is its scalar companion: OMITTED KEEPS the current
   * value — a `Pose` cannot carry the offset, so a caller that says nothing about it is saying
   * nothing about it, not "put the pelvis back at bind height". Undo restores both either way.
   */
  setLivePose(pose: Pose, hipsY?: number): void;
  /**
   * The un-keyed vertical Hips offset, normalized to leg lengths (0 = bind height, + up).
   * Undoable and coalescing exactly like `setLivePoseBone`; a non-finite value is ignored, the
   * way `setPlaybackSpeed` ignores one — a NaN here would travel into a keyframe and be rejected
   * only at save time.
   */
  setLiveHipsY(value: number, options?: { coalesceKey?: string }): void;
  resetLivePose(): void;
  beginPoseDrag(bone: CanonicalBoneId): void;
  endPoseDrag(): void;
  /** Open/close the hips-handle gesture: a whole drag collapses into ONE undo entry. */
  beginHipsDrag(): void;
  endHipsDrag(): void;
  keyBone(bone?: CanonicalBoneId, frame?: number): void;
  keyAllPosed(frame?: number): void;
}

function copyPose(pose: Pose): Pose {
  const out: Pose = {};
  for (const bone of Object.keys(pose) as CanonicalBoneId[]) {
    const q = pose[bone];
    if (q) out[bone] = normalizeQuat(q);
  }
  return out;
}

export function createPoseActions(ctx: ActionContext): PoseActions {
  const writeBone = (bone: CanonicalBoneId, rotation: QuaternionTuple | undefined): void => {
    const pose: Pose = { ...ctx.getState().livePose };
    if (rotation) pose[bone] = rotation;
    else delete pose[bone];
    ctx.set({ livePose: pose });
  };

  const keyBones = (bones: CanonicalBoneId[], frameArg: number | undefined, label: string): void => {
    const state = ctx.getState();
    if (!state.clip) {
      showToast(ctx, 'error', 'Open or create a clip before keyframing.');
      return;
    }
    if (bones.length === 0) {
      // Silence here reads as "the Key button is broken". Say what would make it work instead
      // (keying a selected bone always writes a hold key, even when nothing was posed).
      showToast(ctx, 'info', 'Nothing to key — select a bone to add a hold key.');
      return;
    }

    const frame = Math.max(0, Math.round(frameArg ?? state.playhead.frame));
    const evaluated = evaluatedPose(state, frame);
    let clip = state.clip;
    for (const bone of bones) {
      const rotation = state.livePose[bone] ?? evaluated[bone] ?? identityQuat();
      // The Hips key carries the live height EXPLICITLY. Leaving `hipsY` out would make
      // `setKeyframe` keep whatever the replaced key held (its documented default), so re-keying
      // a Hips rotation after standing the character back up would silently preserve the old
      // crouch — and keying is the one gesture that means "the clip now says what I see".
      clip =
        bone === 'Hips'
          ? setKeyframe(clip, bone, frame, rotation, {
              easing: DEFAULT_EASING,
              hipsY: state.liveHipsY,
            })
          : setKeyframe(clip, bone, frame, rotation, DEFAULT_EASING);
    }
    commitClipEdit(ctx, label, clip);
  };

  const keyBoneImpl = (bone?: CanonicalBoneId, frame?: number): void => {
    if (bone) {
      keyBones([bone], frame, `Key ${bone}`);
      return;
    }
    const state = ctx.getState();
    const target = Math.max(0, Math.round(frame ?? state.playhead.frame));
    const evaluated = evaluatedPose(state, target);
    const posed = (Object.keys(state.livePose) as CanonicalBoneId[]).filter((b) => {
      const live = state.livePose[b];
      if (!live) return false;
      return !quatEquals(live, evaluated[b] ?? identityQuat(), POSE_EPSILON);
    });
    // A crouch is posing even when no rotation moved: dropping the hips and pressing K used to
    // answer "Nothing to key", which reads as a broken button rather than as a missing channel.
    if (
      !posed.includes('Hips') &&
      Math.abs(state.liveHipsY - evaluatedHipsY(state, target)) > POSE_EPSILON
    ) {
      posed.push('Hips');
    }
    keyBones(posed, frame, 'Key pose');
  };

  return {
    setLivePoseBone(bone, rotation, options): void {
      const previous = ctx.getState().livePose[bone];
      const next = normalizeQuat(rotation);
      const key = options?.coalesceKey ?? `pose:${bone}`;

      writeBone(bone, next);
      ctx.history.push({
        label: `Rotate ${bone}`,
        key,
        // Named slice: an IK drag coalesces this together with the chain's target command, and
        // one entry has to be able to restore both.
        slice: `pose:${bone}`,
        undo: () => writeBone(bone, previous),
        redo: () => writeBone(bone, next),
      });
    },

    setLivePose(pose, hipsY): void {
      const state = ctx.getState();
      const previous = state.livePose;
      const previousHipsY = state.liveHipsY;
      const next = copyPose(pose);
      // A non-finite argument is ignored exactly as in `setLiveHipsY`, and an omitted one keeps
      // the current height — the two slices are independent, and one undo entry restores both.
      const nextHipsY =
        hipsY !== undefined && Number.isFinite(hipsY) ? hipsY : previousHipsY;
      ctx.set({ livePose: next, liveHipsY: nextHipsY });
      ctx.history.push({
        label: 'Set pose',
        undo: () => ctx.set({ livePose: previous, liveHipsY: previousHipsY }),
        redo: () => ctx.set({ livePose: next, liveHipsY: nextHipsY }),
      });
    },

    setLiveHipsY(value, options): void {
      if (!Number.isFinite(value)) return;
      const previous = ctx.getState().liveHipsY;
      const key = options?.coalesceKey ?? HIPS_Y_COALESCE_KEY;

      ctx.set({ liveHipsY: value });
      ctx.history.push({
        label: 'Hips height',
        key,
        // Its own slice: a hips drag must not collapse into the rotation entry of whatever bone
        // was dragged before it, and vice versa.
        slice: 'hips-y',
        undo: () => ctx.set({ liveHipsY: previous }),
        redo: () => ctx.set({ liveHipsY: value }),
      });
    },

    resetLivePose(): void {
      const state = ctx.getState();
      const previous = state.livePose;
      const previousHipsY = state.liveHipsY;
      const next = evaluatedLive(state.clip, state.playhead.frame);
      ctx.set(next);
      ctx.history.push({
        label: 'Reset pose',
        undo: () => ctx.set({ livePose: previous, liveHipsY: previousHipsY }),
        redo: () => ctx.set(next),
      });
    },

    beginPoseDrag(bone): void {
      ctx.history.beginCoalesce(`Rotate ${bone}`, `pose:${bone}`);
    },

    endPoseDrag(): void {
      ctx.history.endCoalesce();
    },

    beginHipsDrag(): void {
      ctx.history.beginCoalesce('Hips height', HIPS_Y_COALESCE_KEY);
    },

    endHipsDrag(): void {
      ctx.history.endCoalesce();
    },

    keyBone(bone, frame): void {
      keyBoneImpl(bone, frame);
    },

    keyAllPosed(frame): void {
      keyBoneImpl(undefined, frame);
    },
  };
}
